import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import BezierEasing from 'bezier-easing';

import Circle from './Circle';
import Nav from './Nav';

import { paddingStyle, onlyVerticalPaddingStyle } from '../styles/padding';
import { animation, mediaValues } from '../styles/constants';

export const fade = keyframes`
  0% {
    transform: translateY(5rem);
    opacity: 0;
    visibility: hidden;
  }

  100% {
    transform: translateY(0);
    opacity: 1;
    visibility: visible;
  }
`;

const { animationDuration, transitionTimingFn } = animation;

const DEBOUNCE_PERIOD = 300;
const DECIMAL_PLACES = 2;

const SectionContainer = styled.div`
  :first-of-type {
    animation: ${fade} ${animationDuration}ms ${transitionTimingFn} forw;

    > div,
    > section,
    > footer {
      ${onlyVerticalPaddingStyle}
    }
  }

  :not(:first-of-type) {
    > div,
    > section,
    > footer {
      ${paddingStyle};
    }
  }
`;

class Section extends Component {
  static propTypes = {
    isMobile: PropTypes.bool,
    height: PropTypes.number,
    opacity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    innerRef: PropTypes.object.isRequired,
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node
    ]).isRequired
  };

  shouldComponentUpdate(nextProps) {
    if (this.props.isMobile !== nextProps.isMobile) {
      return true;
    }

    if (this.props.isMobile) {
      return this.props.height !== nextProps.height;
    } else {
      return (
        this.props.opacity !== nextProps.opacity ||
        this.props.height !== nextProps.height
      );
    }
  }

  render() {
    const { innerRef, opacity, height, children } = this.props;

    return (
      <SectionContainer ref={innerRef} style={{ opacity, height }}>
        {children}
      </SectionContainer>
    );
  }
}

const Main = styled.main`
  position: relative;
`;

class Home extends Component {
  static propTypes = {
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node
    ]).isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      activeSectionIndex: 0,
      sectionChangeProgress: 1,
      circleRotationIndex: 0,
      sectionHeights: Array.from(React.Children.count(props.children)),
      sectionOffsetRanges: Array.from(React.Children.count(props.children)),
      previousScrollY: 0,
      applyFade: true,
      windowHeight: 0
    };

    // [Style] Change the bezier curve to adapt opacity fade
    this.opacityEasing = BezierEasing(0, 1, 1, 1);
    this.reverseOpacityEasing = BezierEasing(0.23, 0, 0.5, 0);
    this.sections = React.Children.map(props.children, React.createRef);
    this.handleScroll = throttle(this.handleScroll, 50, {
      leading: true,
      trailing: true
    }).bind(this);
    this.handleSectionResize = this.handleSectionResize.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.state.applyFade !== nextState.applyFade) {
      return true;
    }

    if (this.state.applyFade) {
      return (
        this.state.sectionChangeProgress !== nextState.sectionChangeProgress ||
        this.state.sectionOffsetRanges !== nextState.sectionOffsetRanges ||
        this.state.activeSectionIndex !== nextState.activeSectionIndex ||
        this.state.sectionChangeProgress !== nextState.sectionChangeProgress ||
        this.state.circleRotationIndex !== nextState.circleRotationIndex
      );
    } else {
      return this.state.circleRotationIndex !== nextState.circleRotationIndex;
    }
  }

  handleSectionResize() {
    const windowWidth =
      window.innerWidth || document.documentElement.clientWidth;

    // only resize when width is changed
    if (this.state.windowWidth === windowWidth) {
      return;
    }

    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const pageHeight =
      document.documentElement.scrollHeight ||
      document.documentElement.offsetHeight;

    const sectionHeights = this.sections.map(s => {
      const sectionHeight = s.current.offsetHeight;
      const sectionFitsInWindow = windowHeight >= sectionHeight;
      const height = sectionFitsInWindow ? windowHeight : sectionHeight;
      return {
        sectionFitsInWindow,
        scrollDistance: sectionFitsInWindow ? windowHeight : sectionHeight,
        height
      };
    });

    let sectionStart = 2;
    let circleThresholdStart = Math.round(sectionHeights[0].height / 2);
    const sectionOffsetRanges = [
      {
        sectionRange: [0, 1],
        circleThresholdRange: [0, circleThresholdStart]
      }
    ];

    for (let i = 1; i < sectionHeights.length; i += 1) {
      const scrollDistance = sectionHeights[i - 1].scrollDistance;
      const height = sectionHeights[i].height;

      let sectionEnding = sectionStart + scrollDistance;
      let circleThresholdEnding = Math.round(circleThresholdStart + height);

      if (i === sectionHeights.length - 1) {
        sectionEnding = pageHeight;
        circleThresholdEnding = pageHeight;
      }

      const sectionRange = [sectionStart, sectionEnding + 1];
      const circleThresholdRange = [
        circleThresholdStart,
        circleThresholdEnding + 1
      ];

      sectionStart = sectionEnding + 2;
      circleThresholdStart = circleThresholdEnding + 2;

      sectionOffsetRanges.push({
        sectionRange,
        circleThresholdRange
      });
    }

    this.setState(
      {
        sectionHeights,
        sectionOffsetRanges,
        applyFade: windowWidth > mediaValues.tabletLandscape,
        windowWidth
      },
      this.handleScroll
    );
  }

  handleScroll() {
    const {
      activeSectionIndex,
      sectionOffsetRanges,
      circleRotationIndex,
      previousScrollY,
      applyFade
    } = this.state;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    // Verify if we need to change the circle rotation
    let currentActiveRotationIndex = sectionOffsetRanges.findIndex(
      ({ circleThresholdRange: [rangeStart, rangeEnd] }) =>
        scrollY >= rangeStart && scrollY <= rangeEnd
    );

    // Duck typing if we're somehow out of range
    if (currentActiveRotationIndex === -1) {
      currentActiveRotationIndex = circleRotationIndex;
    }

    if (currentActiveRotationIndex !== circleRotationIndex) {
      this.setState({
        previousScrollY: scrollY,
        circleRotationIndex: currentActiveRotationIndex
      });
    }

    if (applyFade) {
      // Verify if we need to change the active section
      let currentActiveSectionIndex = sectionOffsetRanges.findIndex(
        ({ sectionRange: [rangeStart, rangeEnd] }) =>
          scrollY >= rangeStart && scrollY <= rangeEnd
      );

      // Duck typing if we're somehow out of range
      if (currentActiveSectionIndex === -1) {
        currentActiveSectionIndex = activeSectionIndex;
      }

      if (currentActiveSectionIndex !== activeSectionIndex) {
        this.setState({
          sectionChangeProgress:
            currentActiveSectionIndex > activeSectionIndex ? 0 : 1,
          activeSectionIndex: currentActiveSectionIndex,
          previousScrollY: scrollY
        });
      }

      if (!this.sections[currentActiveSectionIndex].current) {
        return null;
      }

      const isScrollingForward = scrollY > previousScrollY;
      const activeSectionTop = this.sections[
        currentActiveSectionIndex
      ].current.getBoundingClientRect().top;
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight;

      const sectionChangeProgress = 1 - activeSectionTop / windowHeight;

      if (
        sectionChangeProgress >= 0 &&
        ((isScrollingForward &&
          currentActiveSectionIndex < this.sections.length) ||
          (!isScrollingForward && currentActiveSectionIndex > 0))
      ) {
        this.setState({
          sectionChangeProgress: sectionChangeProgress.toFixed(DECIMAL_PLACES),
          previousScrollY: scrollY
        });
      } else {
        this.setState({ previousScrollY: scrollY });
      }
    }
  }

  handleResize = debounce(this.handleSectionResize, DEBOUNCE_PERIOD, {
    leading: false,
    trailing: true
  }).bind(this);

  componentDidMount() {
    this.handleSectionResize();
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
  }

  render() {
    const { children } = this.props;
    const {
      activeSectionIndex,
      sectionChangeProgress,
      sectionHeights,
      circleRotationIndex,
      applyFade
    } = this.state;

    return (
      <>
        <Circle type="all" progress={circleRotationIndex} />
        <Main>
          {React.Children.map(children, (child, index) => {
            let opacity = applyFade ? 0 : 1;

            if (applyFade) {
              if (
                index === activeSectionIndex - 1 ||
                index === activeSectionIndex + 1
              ) {
                opacity = 1 - sectionChangeProgress;
              }

              if (index === activeSectionIndex) {
                opacity = sectionChangeProgress;
              }
            }

            return (
              <Section
                key={index}
                innerRef={this.sections[index]}
                isMobile={!applyFade}
                opacity={
                  index === activeSectionIndex
                    ? this.opacityEasing(opacity).toFixed(DECIMAL_PLACES)
                    : this.reverseOpacityEasing(opacity).toFixed(DECIMAL_PLACES)
                }
                height={sectionHeights[index] && sectionHeights[index].height}
              >
                {child}
              </Section>
            );
          })}
        </Main>
        <Nav
          colorIndex={applyFade ? -1 : circleRotationIndex}
          hideOnScroll={false}
        />
      </>
    );
  }
}

export default Home;
